import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import api from "../../utils/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  User,
  HeartPulse,
  Landmark,
  ShieldCheck,
  PenTool,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  CalendarDays,
  Mail,
  Phone,
  MapPin,
  Home,
  CreditCard,
  AlertCircle,
  Loader2,
  Signature,
  Upload,
  Eye,
} from "lucide-react";

interface FormData {
  // Personal Information
  legalFirstName: string;
  legalLastName: string;
  dateOfBirth: string;
  gender: string;
  mobilePhone: string;
  personalEmail: string;
  residentialAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  // Emergency Contacts
  emergencyContact1Name: string;
  emergencyContact1Relationship: string;
  emergencyContact1Phone: string;
  emergencyContact2Name: string;
  emergencyContact2Relationship: string;
  emergencyContact2Phone: string;
  // Banking Details
  accountName: string;
  bsb: string;
  accountNumber: string;
  bankName: string;
  // Work Rights
  workRightsDocumentType: string;
  workRightsEvidenceName: string;
  workRightsEvidenceUrl: string;
  workRightsEvidenceKey: string;
  // Electronic Signature
  fullName: string;
  signatureDate: string;
  agreed: boolean;
  signatureType: string;
  signatureData: string;
}

const STEPS = [
  { id: 1, label: "Personal Information", icon: User },
  { id: 2, label: "Emergency Contacts", icon: HeartPulse },
  { id: 3, label: "Banking Details", icon: Landmark },
  { id: 4, label: "Work Rights", icon: ShieldCheck },
  { id: 5, label: "Electronic Signature", icon: PenTool },
];

const GENDER_OPTIONS = [
  { value: "", label: "Select an option" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
];

const STATE_OPTIONS = [
  { value: "", label: "Select an option" },
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NT", label: "Northern Territory" },
];

const RELATIONSHIP_OPTIONS = [
  { value: "", label: "Select relationship" },
  { value: "spouse", label: "Spouse/Partner" },
  { value: "parent", label: "Parent" },
  { value: "sibling", label: "Sibling" },
  { value: "child", label: "Child" },
  { value: "friend", label: "Friend" },
  { value: "other", label: "Other" },
];

const WORK_RIGHTS_DOCUMENT_OPTIONS = [
  { value: "", label: "Select document type" },
  { value: "drivers-licence", label: "Driver's Licence" },
  { value: "birth-certificate", label: "Birth Certificate" },
  { value: "passport", label: "Passport" },
];

const NewStarterForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // HR preview mode: renders the hire's submitted form read-only.
  const previewRequestId = searchParams.get("requestId");
  const isPreview = !!previewRequestId;
  const portalPath = isPreview
    ? `/my-onboarding?requestId=${previewRequestId}`
    : "/my-onboarding";
  const readOnly = isPreview;
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    legalFirstName: "",
    legalLastName: "",
    dateOfBirth: "",
    gender: "",
    mobilePhone: "",
    personalEmail: "",
    residentialAddress: "",
    suburb: "",
    state: "",
    postcode: "",
    emergencyContact1Name: "",
    emergencyContact1Relationship: "",
    emergencyContact1Phone: "",
    emergencyContact2Name: "",
    emergencyContact2Relationship: "",
    emergencyContact2Phone: "",
    accountName: "",
    bsb: "",
    accountNumber: "",
    bankName: "",
    workRightsDocumentType: "",
    workRightsEvidenceName: "",
    workRightsEvidenceUrl: "",
    workRightsEvidenceKey: "",
    fullName: "",
    signatureDate: new Date().toISOString().split("T")[0],
    agreed: false,
    signatureType: "Draw",
    signatureData: "",
  });

  // On-screen signature state (mirrors the AssignAssets signature capture)
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [typedSignature, setTypedSignature] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Work rights evidence upload state
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const evidenceFileInputRef = React.useRef<HTMLInputElement>(null);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(
      (touch.clientX - rect.left) * scaleX,
      (touch.clientY - rect.top) * scaleY
    );
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    ctx.lineTo(
      (touch.clientX - rect.left) * scaleX,
      (touch.clientY - rect.top) * scaleY
    );
    ctx.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleConfirmSignature = () => {
    if (formData.signatureType === "Type") {
      if (!typedSignature.trim()) {
        toast.error("Please enter your name");
        return;
      }
      updateField("signatureData", typedSignature.trim());
    } else {
      if (canvasRef.current) {
        updateField("signatureData", canvasRef.current.toDataURL());
      }
    }
    setIsSignatureDialogOpen(false);
  };

  // Upload the selected evidence file to S3, then store its reference in form data
  // (same /api/upload/s3 flow used for licence uploads in MyOnboardingPortal)
  const handleEvidenceFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    setUploadingEvidence(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("image", file);
      uploadFormData.append("context", "work-rights");
      const result = await api.post<{ url: string; key?: string; filename?: string }>(
        "/api/upload/s3",
        uploadFormData
      );
      if (!result?.url) throw new Error("File upload failed");

      setFormData((prev) => ({
        ...prev,
        workRightsEvidenceName: file.name,
        workRightsEvidenceUrl: result.url,
        workRightsEvidenceKey: result.key || result.filename || "",
      }));
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      // The API wrapper already toasts the server's error message
      console.error("Error uploading evidence:", error);
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleRemoveEvidence = () => {
    setFormData((prev) => ({
      ...prev,
      workRightsEvidenceName: "",
      workRightsEvidenceUrl: "",
      workRightsEvidenceKey: "",
    }));
  };

  // HR preview only: fetch a fresh signed URL for the uploaded work rights
  // evidence and open it in a new tab. The URL captured at upload time is a
  // presigned S3 URL that expires after 1h, so reads re-sign from the stored key.
  const [viewingEvidence, setViewingEvidence] = useState(false);
  const handleViewEvidence = async () => {
    if (!previewRequestId) return;
    setViewingEvidence(true);
    try {
      const res = await api.get<{ success: boolean; url?: string }>(
        `/api/onboarding/${previewRequestId}/forms/new-starter-form/attachment?mode=view`
      );
      if (res.success && res.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
      }
    } catch (error: any) {
      // The API wrapper toasts most server errors, but stays silent on 403.
      if (error?.status === 403) {
        toast.error("You don't have permission to view this document");
      }
      console.error("Error viewing evidence:", error);
    } finally {
      setViewingEvidence(false);
    }
  };

  // Load existing form data on mount. HR preview mode (?requestId=...) reads
  // the hire's submitted data from the CASL-gated HR endpoint instead of the
  // caller's own portal record.
  useEffect(() => {
    const loadFormData = async () => {
      try {
        if (previewRequestId) {
          const res = await api.get<{
            success: boolean;
            data: {
              formData: Partial<FormData>;
              isCompleted: boolean;
              submittedAt: string | null;
            };
          }>(
            `/api/onboarding/${previewRequestId}/forms/new-starter-form/submission`,
            { skipErrorToast: true }
          );
          if (res.success && res.data) {
            setFormData((prev) => ({ ...prev, ...(res.data.formData || {}) }));
          }
        } else {
          const res = await api.get<{
            success: boolean;
            data: {
              formData: Partial<FormData> & { lastSavedStep?: number };
              formsStatus: string;
              prefill?: Partial<FormData>;
            };
          }>(
            "/api/onboarding/my-portal/form",
            { skipErrorToast: true }
          );
          if (res.success && res.data) {
            // Merge order: blank defaults < details we already know about the
            // candidate (prefill) < answers they have previously saved.
            setFormData((prev) => ({
              ...prev,
              ...(res.data.prefill || {}),
              ...(res.data.formData || {}),
            }));
            if (res.data.formData?.lastSavedStep) {
              setCurrentStep(res.data.formData.lastSavedStep);
            }
          }
        }
      } catch (error) {
        console.error("Error loading form data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadFormData();
  }, [previewRequestId]);

  const updateField = (field: keyof FormData, value: string | boolean) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post<{ success: boolean; message: string }>(
        "/api/onboarding/my-portal/form",
        {
          formData,
          step: currentStep,
          isCompleted: false,
        }
      );
      if (res.success) {
        toast.success("Progress saved");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await api.post<{ success: boolean; message: string }>(
        "/api/onboarding/my-portal/form",
        {
          formData,
          step: currentStep,
          isCompleted: true,
        }
      );
      if (res.success) {
        toast.success("Form submitted successfully!");
        navigate("/my-onboarding?tab=forms");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form");
    } finally {
      setSaving(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return (
          !!formData.legalFirstName.trim() &&
          !!formData.legalLastName.trim() &&
          !!formData.dateOfBirth &&
          !!formData.gender &&
          !!formData.mobilePhone.trim() &&
          !!formData.personalEmail.trim() &&
          !!formData.residentialAddress.trim() &&
          !!formData.suburb.trim() &&
          !!formData.state &&
          !!formData.postcode.trim()
        );
      case 2:
        return (
          !!formData.emergencyContact1Name.trim() &&
          !!formData.emergencyContact1Relationship &&
          !!formData.emergencyContact1Phone.trim()
        );
      case 3:
        return (
          !!formData.accountName.trim() &&
          !!formData.bankName.trim() &&
          !!formData.bsb.trim() &&
          !!formData.accountNumber.trim()
        );
      case 4:
        return (
          !!formData.workRightsDocumentType &&
          !!formData.workRightsEvidenceUrl
        );
      case 5:
        return (
          !!formData.fullName.trim() &&
          !!formData.signatureDate &&
          !!formData.signatureData &&
          formData.agreed
        );
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!readOnly && !isStepValid(currentStep)) {
      toast.error("Please fill in all required fields before continuing");
      return;
    }
    if (currentStep < 5) setCurrentStep((s) => s + 1);
  };

  const canSubmit =
    isStepValid(1) &&
    isStepValid(2) &&
    isStepValid(3) &&
    isStepValid(4) &&
    isStepValid(5);

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const goToStep = (step: number) => {
    if (readOnly || step <= currentStep) setCurrentStep(step);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = step.id === currentStep;
        const isCompleted = step.id < currentStep;
        const isClickable = readOnly || step.id <= currentStep;

        return (
          <React.Fragment key={step.id}>
            <button
              onClick={() => isClickable && goToStep(step.id)}
              disabled={!isClickable}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                isActive
                  ? "bg-slate-900 text-white"
                  : isCompleted
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-gray-100 text-gray-400 border border-gray-200"
              } ${isClickable ? "cursor-pointer" : "cursor-not-allowed"}`}
            >
              {isCompleted ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <Icon className="w-4 h-4" />
              )}
              <span className="hidden md:inline">{step.label}</span>
            </button>
            {index < STEPS.length - 1 && (
              <div
                className={`hidden md:block w-8 h-px ${
                  step.id < currentStep ? "bg-green-400" : "bg-gray-200"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ─── STEP 1: Personal Information ───
  const renderPersonalInfo = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="p-2 bg-slate-100 rounded-lg">
          <User className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
          <p className="text-sm text-gray-500">
            Complete your personal, emergency and banking details
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Legal First Name */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Legal First Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="e.g. Kenny"
            value={formData.legalFirstName}
            onChange={(e) => updateField("legalFirstName", e.target.value)}
            disabled={readOnly}
            className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white"
          />
        </div>

        {/* Legal Last Name */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Legal Last Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="e.g. Watkins"
            value={formData.legalLastName}
            onChange={(e) => updateField("legalLastName", e.target.value)}
            disabled={readOnly}
            className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white"
          />
        </div>

        {/* Date of Birth */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Input
              type="date"
              placeholder="dd/mm/yyyy"
              value={formData.dateOfBirth}
              onChange={(e) => updateField("dateOfBirth", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white pr-10"
            />
            <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Gender */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Gender <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.gender}
            onChange={(e) => updateField("gender", e.target.value)}
            disabled={readOnly}
            className="w-full h-11 rounded-lg bg-gray-50 border border-gray-200 px-3 text-sm text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          >
            {GENDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Mobile Phone */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Mobile Phone <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Input
              placeholder="e.g. 0412 345 678"
              value={formData.mobilePhone}
              onChange={(e) => updateField("mobilePhone", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white pl-10"
            />
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Personal Email */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Personal Email <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Input
              type="email"
              placeholder="e.g. name@email.com"
              value={formData.personalEmail}
              onChange={(e) => updateField("personalEmail", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white pl-10"
            />
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Residential Address */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Residential Address <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Input
              placeholder="Street address"
              value={formData.residentialAddress}
              onChange={(e) => updateField("residentialAddress", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white pl-10"
            />
            <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Suburb */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Suburb <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder=""
            value={formData.suburb}
            onChange={(e) => updateField("suburb", e.target.value)}
            disabled={readOnly}
            className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white"
          />
        </div>

        {/* State */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            State <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.state}
            onChange={(e) => updateField("state", e.target.value)}
            disabled={readOnly}
            className="w-full h-11 rounded-lg bg-gray-50 border border-gray-200 px-3 text-sm text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          >
            {STATE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Postcode */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Postcode <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="e.g. 2000"
            value={formData.postcode}
            onChange={(e) => updateField("postcode", e.target.value)}
            disabled={readOnly}
            className="h-11 rounded-lg bg-gray-50 border-gray-200 focus:bg-white"
          />
        </div>
      </div>
    </div>
  );

  // ─── STEP 2: Emergency Contacts ───
  const renderEmergencyContacts = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="p-2 bg-slate-100 rounded-lg">
          <HeartPulse className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Emergency Contacts</h3>
          <p className="text-sm text-gray-500">
            Provide at least one emergency contact for workplace safety
          </p>
        </div>
      </div>

      {/* Primary Contact */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-white text-slate-700 border-slate-200">
            Primary Contact
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. Jane Watkins"
              value={formData.emergencyContact1Name}
              onChange={(e) => updateField("emergencyContact1Name", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Relationship <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.emergencyContact1Relationship}
              onChange={(e) => updateField("emergencyContact1Relationship", e.target.value)}
              disabled={readOnly}
              className="w-full h-11 rounded-lg bg-white border border-gray-200 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            >
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. 0412 345 678"
              value={formData.emergencyContact1Phone}
              onChange={(e) => updateField("emergencyContact1Phone", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
        </div>
      </div>

      {/* Secondary Contact */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-white text-gray-500 border-gray-200">
            Secondary Contact (Optional)
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Full Name</label>
            <Input
              placeholder="e.g. John Watkins"
              value={formData.emergencyContact2Name}
              onChange={(e) => updateField("emergencyContact2Name", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Relationship</label>
            <select
              value={formData.emergencyContact2Relationship}
              onChange={(e) => updateField("emergencyContact2Relationship", e.target.value)}
              disabled={readOnly}
              className="w-full h-11 rounded-lg bg-white border border-gray-200 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            >
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Phone Number</label>
            <Input
              placeholder="e.g. 0412 345 678"
              value={formData.emergencyContact2Phone}
              onChange={(e) => updateField("emergencyContact2Phone", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // ─── STEP 3: Banking Details ───
  const renderBankingDetails = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="p-2 bg-slate-100 rounded-lg">
          <Landmark className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Banking Details</h3>
          <p className="text-sm text-gray-500">
            Your bank account details for payroll
          </p>
        </div>
      </div>

      {/* Bank Account */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-gray-900">Bank Account</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Account Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. Kenny Watkins"
              value={formData.accountName}
              onChange={(e) => updateField("accountName", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. Commonwealth Bank"
              value={formData.bankName}
              onChange={(e) => updateField("bankName", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              BSB <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. 062-000"
              value={formData.bsb}
              onChange={(e) => updateField("bsb", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Account Number <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. 1234 5678"
              value={formData.accountNumber}
              onChange={(e) => updateField("accountNumber", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg bg-white border-gray-200"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // ─── STEP 4: Work Rights ───
  const renderWorkRights = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="p-2 bg-slate-100 rounded-lg">
          <ShieldCheck className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Work Rights</h3>
          <p className="text-sm text-gray-500">
            Confirm your entitlement to work in Australia and upload your evidence
          </p>
        </div>
      </div>

      {/* Declaration */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-gray-900">Work Rights Declaration</span>
        </div>
        <p className="text-sm text-gray-600">
          I declare that I am legally entitled to work in Australia and can provide proof of work rights
          (Australian passport, birth certificate, or valid visa) if requested.
        </p>
      </div>

      {/* Evidence Upload */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-gray-900">Evidence of Work Rights</span>
        </div>

        <div className="space-y-1.5 max-w-md">
          <label className="text-sm font-medium text-gray-700">
            Document Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.workRightsDocumentType}
            onChange={(e) => updateField("workRightsDocumentType", e.target.value)}
            disabled={readOnly}
            className="w-full h-11 rounded-lg bg-white border border-gray-200 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          >
            {WORK_RIGHTS_DOCUMENT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Upload Document <span className="text-red-500">*</span>
          </label>
          <input
            ref={evidenceFileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
            className="hidden"
            onChange={handleEvidenceFileSelected}
          />
          {formData.workRightsEvidenceUrl ? (
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600 shrink-0">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{formData.workRightsEvidenceName}</p>
                  <p className="text-xs text-gray-500">Uploaded</p>
                </div>
              </div>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl px-3"
                  onClick={handleRemoveEvidence}
                >
                  Remove
                </Button>
              )}
              {isPreview && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl px-3"
                  onClick={handleViewEvidence}
                  disabled={viewingEvidence}
                >
                  {viewingEvidence ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-1.5" />
                  )}
                  View
                </Button>
              )}
            </div>
          ) : !readOnly ? (
            <Button
              type="button"
              variant="outline"
              disabled={uploadingEvidence}
              className="w-full rounded-xl border-dashed border-gray-200 h-20 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-slate-700 hover:border-slate-400 hover:bg-gray-50 transition-all"
              onClick={() => evidenceFileInputRef.current?.click()}
            >
              {uploadingEvidence ? (
                <>
                  <Loader2 size={24} className="animate-spin" />
                  <span className="text-xs font-bold uppercase tracking-widest">Uploading...</span>
                </>
              ) : (
                <>
                  <Upload size={24} />
                  <span className="text-xs font-bold uppercase tracking-widest">Upload Evidence</span>
                </>
              )}
            </Button>
          ) : (
            <p className="text-sm text-gray-500">No document uploaded</p>
          )}
          <p className="text-xs text-gray-500">
            Accepted formats: JPG, PNG or PDF (max 10MB). Upload a clear photo or scan of your document.
          </p>
        </div>
      </div>
    </div>
  );

  // ─── STEP 5: Electronic Signature ───
  const renderElectronicSignature = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="p-2 bg-slate-100 rounded-lg">
          <PenTool className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Electronic Signature</h3>
          <p className="text-sm text-gray-500">
            Review and sign your new starter form declaration
          </p>
        </div>
      </div>

      {/* Declaration Summary */}
      <div className="bg-gray-50 rounded-xl p-5 space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Declaration Summary</h4>
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            I, <strong>{formData.legalFirstName} {formData.legalLastName}</strong>, declare that:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>The information I have provided in this form is true and correct.</li>
            <li>I understand that providing false or misleading information is a serious offence.</li>
            <li>I understand my bank details will be used for payroll processing.</li>
            <li>I consent to my emergency contacts being used in case of a workplace emergency.</li>
          </ul>
        </div>
      </div>

      {/* Signature Fields */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Full Legal Name for Signature <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Type your full legal name"
              value={formData.fullName}
              onChange={(e) => updateField("fullName", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg border-gray-200"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Date <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              value={formData.signatureDate}
              onChange={(e) => updateField("signatureDate", e.target.value)}
              disabled={readOnly}
              className="h-11 rounded-lg border-gray-200"
            />
          </div>
        </div>

        {/* Digital Signature */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">
            Digital Signature <span className="text-red-500">*</span>
          </label>
          {formData.signatureData ? (
            <div className="relative p-4 rounded-xl bg-gray-50 border border-gray-200 border-dashed flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600 shrink-0">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                {formData.signatureType === "Draw" ? (
                  <img src={formData.signatureData} alt="Signature" className="max-h-10" />
                ) : (
                  <span className="font-signature text-xl text-gray-800">{formData.signatureData}</span>
                )}
              </div>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl px-3"
                  onClick={() => updateField("signatureData", "")}
                >
                  Change
                </Button>
              )}
            </div>
          ) : !readOnly ? (
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-xl border-dashed border-gray-200 h-20 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-slate-700 hover:border-slate-400 hover:bg-gray-50 transition-all"
              onClick={() => setIsSignatureDialogOpen(true)}
            >
              <Signature size={24} />
              <span className="text-xs font-bold uppercase tracking-widest">Capture Signature</span>
            </Button>
          ) : (
            <p className="text-sm text-gray-500">No signature captured</p>
          )}
        </div>

        {/* Agreement Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer p-4 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            checked={formData.agreed}
            onChange={(e) => updateField("agreed", e.target.checked)}
            disabled={readOnly}
            className="mt-1 w-4 h-4 rounded border-gray-300 text-slate-900 focus:ring-slate-900"
          />
          <div>
            <p className="text-sm font-medium text-gray-900">
              I agree to the declaration above
            </p>
            <p className="text-xs text-gray-500 mt-1">
              By checking this box and providing my signature above, I agree that this constitutes my
              electronic signature and is legally binding.
            </p>
          </div>
        </label>
      </div>

      {/* Submit Button (hidden in HR preview — read-only) */}
      {!readOnly && (
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            className="h-11 px-8 bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Submit Form
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderPersonalInfo();
      case 2:
        return renderEmergencyContacts();
      case 3:
        return renderBankingDetails();
      case 4:
        return renderWorkRights();
      case 5:
        return renderElectronicSignature();
      default:
        return renderPersonalInfo();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-slate-900" />
            <p className="text-sm text-gray-500">Loading your form...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(portalPath)}
                aria-label="Back to Portal"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>
                  Back<span className="hidden sm:inline"> to Portal</span>
                </span>
              </button>
              <div className="h-6 w-px bg-gray-200 hidden sm:block" />
              <div>
                <h1 className="text-base font-semibold text-gray-900">New Starter Form</h1>
                <p className="text-xs text-gray-500">
                  Step {currentStep} of 5 — {STEPS[currentStep - 1].label}
                </p>
              </div>
            </div>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Progress"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="p-6 md:p-8">
            {renderStepContent()}
          </div>

          {/* Navigation Footer */}
          <div className="px-6 md:px-8 py-5 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                currentStep === 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <span className="text-sm text-gray-400">
              {currentStep} / 5
            </span>

            {readOnly && currentStep === 5 ? (
              <button
                onClick={() => navigate(portalPath)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors"
              >
                Back to Portal
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={currentStep === 5}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  currentStep === 5
                    ? "text-gray-300 cursor-not-allowed"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Signature Capture Dialog */}
      <Dialog open={isSignatureDialogOpen} onOpenChange={setIsSignatureDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Your Signature</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.signatureType === "Type" ? "default" : "outline"}
                onClick={() => updateField("signatureType", "Type")}
                className="flex-1 rounded-xl"
              >
                Type
              </Button>
              <Button
                type="button"
                variant={formData.signatureType === "Draw" ? "default" : "outline"}
                onClick={() => updateField("signatureType", "Draw")}
                className="flex-1 rounded-xl"
              >
                Draw
              </Button>
            </div>

            {formData.signatureType === "Type" ? (
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700">Enter full name:</label>
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Your Full Name"
                  className="font-signature text-lg rounded-xl h-12"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700">Draw signature:</label>
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  className="w-full border rounded-2xl cursor-crosshair bg-gray-50"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={stopDrawing}
                />
                <Button type="button" variant="outline" size="sm" onClick={clearCanvas} className="rounded-lg">
                  Clear
                </Button>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsSignatureDialogOpen(false)} className="rounded-xl">Cancel</Button>
              <Button type="button" onClick={handleConfirmSignature} className="bg-black text-white rounded-xl">Confirm Signature</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewStarterForm;
